/**
 * The counter a faucet must stay on, read from a PERSISTED vanity assembly.
 *
 * `createSinkFaucet` derives a counter-mount faucet's setback ENTIRELY from
 * the sink — for the default undermount that puts the faucet's back face 9.9"
 * behind the counter centre, so anything shallower than ~19.7" pushes the body
 * through the wall behind it. It already clamps against a `countertop`
 * container for exactly that reason, but only the CREATION paths
 * (`assembleVanity`, `bathroom-post-processing`, the standalone-sink builders)
 * ever passed one. The SWAP paths did not, so re-seating a faucet — or sliding
 * one along with a re-seated sink — could still bury it in the wall.
 *
 * The creation paths get the container for free: they pass the countertop they
 * have just built, whose frame is the cabinet's by construction. A persisted
 * countertop is not so lucky, which is what this module exists for.
 */

const { alongWallAxis } = require('../movement/wallAlignedAxis');

/**
 * Resolve `{ center, depth }` for `createSinkFaucet`'s `countertop` argument.
 *
 * **The depth axis is not reliably `z`.** 60 of 331 vanity pairs in dev store
 * the cabinet local-frame at ry = ±90° while the countertop sits world-axis at
 * ry = 0, so that countertop's `dimensions.z` is its WIDTH. Handing a 60"
 * width in as the depth makes the clamp a no-op on a 21" counter; the mirror
 * case hands a 21" depth in for a 60" run and would drag every faucet back to
 * the counter's edge. Resolve the axis against the direction the vanity RUNS —
 * the cabinet is reliably local-frame, so its rotation gives that direction.
 * Same trap, same resolution as `swap_fixture_inplace`'s sink-fit gate
 * (issue #1411).
 *
 * Returns `null` when there is no usable countertop or cabinet. That leaves
 * `createSinkFaucet` on its pre-existing unclamped behaviour, which is the
 * right failure: a bound invented from a missing counter would move faucets
 * that are fine.
 *
 * @param {Object} countertop - the assembly's persisted countertop
 * @param {Object} cabinet    - the assembly's persisted vanity cabinet
 * @returns {{center: {x: number, z: number}, depth: number}|null}
 */
function resolveCounterContainer(countertop, cabinet) {
  const ctDims = countertop?.dimensions;
  const ctCenter = countertop?.center;
  if (!ctDims || !ctCenter) return null;
  if (!cabinet?.rotation && cabinet?.rotation !== 0) {
    // No cabinet to resolve the run direction against. `z` would be a guess.
    return null;
  }

  const cabinetRotY = cabinet.rotation?.y ?? 0;
  const vanityRunDir = { x: Math.cos(cabinetRotY), z: Math.sin(cabinetRotY) };
  const widthAxis = alongWallAxis(countertop.rotation?.y, vanityRunDir);
  const depthAxis = widthAxis === 'x' ? 'z' : 'x';
  const depth = ctDims[depthAxis] ?? ctDims.depth;

  if (!Number.isFinite(depth) || depth <= 0) return null;

  // The bound must describe the WALL, and the wall is at the CABINET's back
  // plane. `buildVanityCountertop` overhangs the sides and the front only —
  // the front overhang shifts `centerOffset` forward rather than growing the
  // slab backwards — so for anything it built the two back edges coincide, and
  // clipping here changes nothing. They do NOT coincide on all persisted data:
  // 51 of 354 vanity pairs in dev have the countertop's back edge behind the
  // cabinet's, 0.49" (a symmetric overhang) at the 95th percentile. Taking the
  // counter's edge there would license a faucet half an inch into the wall,
  // which is the shape this container exists to prevent.
  const cabDims = cabinet.dimensions;
  if (cabDims?.z > 0 && cabinet.center) {
    const s = Math.sin(cabinetRotY);
    const c = Math.cos(cabinetRotY);
    const project = (pt) => (pt.x || 0) * s + (pt.z || 0) * c;
    const cabinetBack = project(cabinet.center) - cabDims.z / 2;
    const counterBack = project(ctCenter) - depth / 2;
    if (counterBack < cabinetBack) {
      // Keep the FRONT edge (the counter legitimately overhangs there) and
      // move the back edge up to the cabinet's: re-centre and shrink by the
      // difference.
      const trim = cabinetBack - counterBack;
      return {
        center: {
          x: ctCenter.x + (trim / 2) * s,
          z: ctCenter.z + (trim / 2) * c,
        },
        depth: depth - trim,
      };
    }
  }

  return { center: { x: ctCenter.x, z: ctCenter.z }, depth };
}

/**
 * Pull an object forward until its back face clears the counter's back edge.
 *
 * The post-hoc twin of the clamp inside `createSinkFaucet`: that one bounds the
 * setback BEFORE the centre is computed, this one corrects a centre that
 * already exists — which is what a slide needs, since `swapSinkModel` carries
 * counter-mounted faucets along with a re-seated sink by translating them
 * rather than rebuilding them. `counterContainer.ci.test.js` pins that the two
 * agree on the same inputs.
 *
 * Never pushes an object PAST `noCloserThan` (the sink centre, for a faucet) —
 * on a counter with no room at all the bound goes negative, and the honest
 * answer is "as far forward as the setback rule ever goes", not "out in front
 * of the basin". `createSinkFaucet`'s `Math.max(0, …)` floor says the same.
 *
 * Returns the input centre unchanged (same object identity) when nothing is
 * out of bounds, so callers can use it as a cheap no-op guard.
 *
 * @param {Object}  args
 * @param {{x,z}}   args.center      - the object's current centre
 * @param {number}  args.depth       - its extent along the vanity's depth axis
 * @param {number}  args.rotationY   - the vanity's rotation (radians)
 * @param {Object}  args.counter     - from `resolveCounterContainer`
 * @param {{x,z}}   [args.noCloserThan] - never move the centre in front of this
 * @returns {{x: number, z: number}}
 */
function containOnCounter({ center, depth, rotationY, counter, noCloserThan }) {
  if (!counter?.depth || !center) return center;
  // `u` points AWAY from the wall, into the room — the same axis
  // `createSinkFaucet` subtracts its setback along.
  const s = Math.sin(rotationY || 0);
  const c = Math.cos(rotationY || 0);
  const project = (p) => (p.x || 0) * s + (p.z || 0) * c;

  const counterBack = project(counter.center) - counter.depth / 2;
  const objectBack = project(center) - (depth || 0) / 2;
  let forward = counterBack - objectBack;
  if (forward <= 0) return center;

  if (noCloserThan) {
    // Cap at the reference point so a counter too shallow for ANY setback
    // parks the faucet on the basin centre rather than in front of it.
    const maxForward = project(noCloserThan) - project(center);
    if (maxForward <= 0) return center;
    forward = Math.min(forward, maxForward);
  }

  return { ...center, x: center.x + forward * s, z: center.z + forward * c };
}

module.exports = { resolveCounterContainer, containOnCounter };
